import {
    applicationStatusOf,
    decisionOutcome,
    programName,
} from '@/shared/utils/applicationStatus';

const ROLE_NOUN = {
    la: ['LA', 'LAs'],
    si: ['SI leader', 'SI leaders'],
    ta: ['TA', 'TAs'],
};

const isCount = (value) => Number.isInteger(value) && value >= 1;

function nounFor(programType, count) {
    const forms = ROLE_NOUN[programType];
    if (!forms) return null;
    return count === 1 ? forms[0] : forms[1];
}

// An award that stands, as hasStandingAward reads it, and that is settled:
// a pending contingent award leaves the request "Decided", not approved
const settlesAward = (decision) =>
    decision.finalStatus !== 'voided' &&
    (['approved', 'partial'].includes(decision.type) ||
        (decision.type === 'contingent' &&
            decision.finalStatus === 'finalized'));

/**
 * The decision that put an approved request in place, from the decisions the
 * server sent this viewer, or null.
 */
export function awardOf(application) {
    if (application?.status !== 'approved') return null;
    return (application.decisions || []).find(settlesAward) || null;
}

/**
 * A request's one status, as faculty read it in its header and in their list.
 * An approved request says how much was awarded: "Approved: 3 LAs", or
 * "Partially approved · 2 of 3 LAs awarded" when the program awarded fewer than asked.
 * Every other status reads as the shared status map has it.
 */
export function requestOutcomeOf(application) {
    const base = applicationStatusOf(application?.status, application);
    const award = awardOf(application);
    if (!award) return { bg: base.bg, label: base.label };
    const { awardedCount, requestedCount, programType } = award;
    if (!isCount(awardedCount) || !nounFor(programType, awardedCount)) {
        return { bg: base.bg, label: decisionOutcome(award).label };
    }
    if (isCount(requestedCount) && awardedCount < requestedCount) {
        return {
            bg: base.bg,
            label: `Partially approved \u00b7 ${awardedCount} of ${requestedCount} ${nounFor(programType, requestedCount)} awarded`,
        };
    }
    return {
        bg: base.bg,
        label: `Approved: ${awardedCount} ${nounFor(programType, awardedCount)}`,
    };
}

/** Whether the request is returned to its faculty member. */
export function needsFacultyAction(application) {
    return application?.status === 'revision_requested';
}

/** Whether `viewer` (the signed-in profile) filed the request. */
export function isFiledBy(application, viewer) {
    const applicantId = application?.applicant?._id ?? application?.applicant;
    return !!viewer?._id && !!applicantId && applicantId === viewer._id;
}

/**
 * Whether the request waits on this viewer to edit and resubmit it. Only the
 * person who filed it can: the server refuses an update from anyone else, the
 * instructor of record included.
 */
export function canResubmit(application, viewer) {
    return needsFacultyAction(application) && isFiledBy(application, viewer);
}

/**
 * What a returned request tells someone who cannot resubmit it, such as the
 * instructor of record it names; null when the viewer can resubmit it.
 */
export function returnedNoteFor(application, viewer) {
    if (!needsFacultyAction(application) || isFiledBy(application, viewer)) {
        return null;
    }
    const filer = application.applicant?.name;
    return filer
        ? `Returned for changes. ${filer} filed this request and can resubmit it.`
        : 'Returned for changes. The person who filed this request can resubmit it.';
}

/**
 * What the program said, for the top of the request page: the notes on a
 * returned request, or each released decision's next step and message.
 */
export function programMessagesOf(application) {
    if (!application) return [];
    if (application.status === 'revision_requested') {
        return application.revisionNotes
            ? [
                  {
                      key: 'revision',
                      heading: 'What the program asked you to change',
                      text: application.revisionNotes,
                  },
              ]
            : [];
    }
    return (application.decisions || [])
        .filter((d) => d.finalStatus !== 'voided')
        .map((decision) => {
            const outcome = decisionOutcome(decision);
            return {
                key: decision._id,
                heading: `From the ${programName(decision.programType)} program`,
                nextStep: outcome.nextStep,
                text: decision.messageToFaculty || null,
            };
        })
        .filter((m) => m.nextStep || m.text);
}
